import { type Component, markRaw } from 'vue'

import RecommendationAlgoliaCarousel, {
  type RecommendationAlgoliaCarouselProps,
} from '@backmarket/nuxt-layer-recommendation/RecommendationAlgoliaCarousel.vue'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

export type BrazeContentCard = {
  expiresAt: Date | null
  id?: string
  extras: Record<string, string>
  isControl: boolean
  pinned: boolean
  updated: Date | null
  viewed: boolean
}

// Define supported ContentCard types in one place for easy extension
// When adding a new type, add it here and add a corresponding case in mapPropsByBlockType
type SupportedContentCardType = RecommendationAlgoliaCarouselProps

export type ContentCard = {
  id: string
  title: string
  icon?: string
  tag: Component
  props: SupportedContentCardType
}

function mapPropsByBlockType({
  type,
  props,
}: {
  type: string
  props: Record<string, string>
}) {
  const logger = useLogger()

  if (!props.title) {
    logger.error(
      `[PERSONALIZED_CONTENT_CARD] Missing title for content card type: ${type}`,
      {
        owners: ['bot-squad-content-front'],
      },
    )

    return undefined
  }

  switch (type) {
    case 'recommendations':
      if (!props.product_id) {
        logger.error(
          '[PERSONALIZED_CONTENT_CARD] Missing product_id for content card type: recommendations',
          {
            owners: ['bot-squad-content-front'],
          },
        )

        return undefined
      }
      if (
        ['bought-together', 'related-products'].includes(
          props.recommendation_type,
        ) === false
      ) {
        logger.error(
          '[PERSONALIZED_CONTENT_CARD] Unsupported recommendation_type for content card type: recommendations',
          {
            owners: ['bot-squad-content-front'],
          },
        )

        return undefined
      }

      return {
        title: props.title,
        tag: markRaw(RecommendationAlgoliaCarousel),
        props: {
          recommendationQuery: {
            objectIds: [props.product_id],
            model:
              props.recommendation_type as RecommendationAlgoliaCarouselProps['recommendationQuery']['model'],
          },
        },
      }
    default:
      logger.error(
        `[PERSONALIZED_CONTENT_CARD] Unsupported content card type: ${type}`,
        {
          owners: ['bot-squad-content-front'],
        },
      )

      return undefined
  }
}

function mapContentCard(card: BrazeContentCard): ContentCard | undefined {
  const { icon, type, ...props } = card.extras

  const blockPropsResult = mapPropsByBlockType({ type, props })

  if (!blockPropsResult) {
    return undefined
  }

  return {
    // The same content card with the same id can be received multiple times, so we need to generate a unique id for each occurrence
    id: crypto.randomUUID(),
    icon,
    title: blockPropsResult.title,
    tag: blockPropsResult.tag,
    props: blockPropsResult.props,
  }
}

export function transformContentCards({
  cards,
  feed,
}: {
  cards: BrazeContentCard[]
  feed?: string
}): ContentCard[] {
  return cards
    .filter(({ extras }) => extras.feed === feed)
    .map(mapContentCard)
    .filter((card) => !isEmpty(card))
}
